/**
 * Module: WalletCard
 * Role: Balance display card. Fetches /api/v2/buyer/earnings/balance via apiFetch.
 *       Honest empty state when balance=0 or request fails (DA-8 compliance).
 *       NO hardcoded mock balance.
 *
 * Uses:
 *   - components/atoms:Card
 *   - components/atoms:Skeleton
 *   - @tanstack/react-query:useQuery
 *   - api/client:apiFetch
 *   - lib/format:formatKopecks
 *
 * Used by:
 *   - screens/HomeScreen/index
 *
 * Glossary: ai/glossary/buyer.md
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import { Card } from '../../components/atoms';
import { P, S, T } from '../../styles/tokens';
import { formatKopecks } from '../../lib/format';

interface BalanceView {
  available_kopecks: number;
  pending_kopecks?: number;
}

export function WalletCard() {
  const { data, isLoading, isError } = useQuery<BalanceView>({
    queryKey: ['earnings', 'balance'],
    queryFn: () => apiFetch<BalanceView>('/api/v2/buyer/earnings/balance'),
    retry: false,
  });

  // SYMMETRIC LIMIT (ADR-022 §6) — `data-coach="wallet"` mirrors COACH_TARGETS.wallet
  // in screens/onboarding/coachmarks.ts (peers: RecommendationsList "feed", PageHeader "bell").
  // It is the spotlight anchor for the FTR-1114 coachmarks tour. Atom purity forbids
  // importing the SSOT const into the Card atom, so the literal lives here under contract.
  if (isLoading) {
    return (
      <div data-coach="wallet">
        <Card>
          <div
            style={{
              height: 72,
              borderRadius: 8,
              background: P.mist,
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
        </Card>
      </div>
    );
  }

  const available = data?.available_kopecks ?? 0;
  const pending = data?.pending_kopecks ?? 0;
  const hasBalance = !isLoading && !isError && available > 0;

  if (!hasBalance) {
    return (
      // data-coach="wallet" — see SYMMETRIC LIMIT note above (FTR-1114 tour anchor).
      <div data-coach="wallet">
        <Card>
          <p
            style={{
              font: T.headline,
              color: S.secondaryLabel,
              margin: '0 0 4px',
            }}
          >
            Здесь будет ваш заработок
          </p>
          <p
            style={{
              font: T.footnote,
              color: S.tertiaryLabel,
              margin: 0,
            }}
          >
            Завершите задание чтобы получить вознаграждение
          </p>
        </Card>
      </div>
    );
  }

  const balanceRub = formatKopecks(available);
  const pendingRub = pending > 0 ? formatKopecks(pending) : null;

  return (
    // data-coach="wallet" — see SYMMETRIC LIMIT note above (FTR-1114 tour anchor).
    <div data-coach="wallet">
      <Card>
        <p
          style={{
            font: T.footnote,
            color: S.secondaryLabel,
            marginBottom: 4,
            marginTop: 0,
          }}
        >
          Ваш баланс
        </p>
        <p
          style={{
            font: T.heroAmount,
            color: P.ink,
            margin: 0,
            letterSpacing: -0.8,
          }}
        >
          {balanceRub}
        </p>
        {pendingRub && (
          <p
            style={{
              font: T.caption,
              color: S.tertiaryLabel,
              margin: '6px 0 0',
            }}
          >
            + {pendingRub} ожидает начисления
          </p>
        )}
      </Card>
    </div>
  );
}
